import { z } from 'zod';

/**
 * Where one take of a test stands (`03-courses-discounts.md` §Data model).
 *
 * `in_progress → submitted → passed | failed`, and `in_progress → expired` when
 * the TTL sweep finds it abandoned.
 *
 * **`submitted` is the claim (N27), not a courtesy state.** Submitting takes the
 * row out of `in_progress` in one guarded write *before* anything is scored, so
 * a double tap, a retried request and the expiry sweep cannot each score the
 * same answers — and the terminal write carries that claim as its own
 * precondition. Without it, scoring would be check-then-act over a row two
 * callers can both still see as open.
 */
export const TEST_ATTEMPT_STATUSES = [
  'in_progress',
  'submitted',
  'passed',
  'failed',
  'expired',
] as const;

export const TestAttemptStatusSchema = z.enum(TEST_ATTEMPT_STATUSES);

export type TestAttemptStatus = (typeof TEST_ATTEMPT_STATUSES)[number];

const STATUS_SET: ReadonlySet<string> = new Set(TEST_ATTEMPT_STATUSES);

export function isTestAttemptStatus(value: string): value is TestAttemptStatus {
  return STATUS_SET.has(value);
}

/** Whether the member may still answer — the only status a submit may claim from. */
export function isOpenAttemptStatus(status: TestAttemptStatus): boolean {
  return status === 'in_progress';
}

/** Whether the row has stopped moving. `submitted` has not: scoring still owns it. */
export function isTerminalAttemptStatus(status: TestAttemptStatus): boolean {
  return status === 'passed' || status === 'failed' || status === 'expired';
}

/**
 * Whether this attempt occupies one of the test's `attemptsAllowed` slots.
 *
 * **Every status does**, and the total answer is the point — matrix row 8. An
 * exemption for `expired` would make abandoning a free retake: open an attempt,
 * read the questions, walk away, come back with the answers. It is stated in the
 * attempt-start UI rather than discovered afterwards. `in_progress` counts too,
 * so the take in flight holds its own slot and a member cannot open five at once.
 *
 * Written as an exhaustive `true` rather than as `return true` so that adding a
 * status is a decision somebody has to take here, in front of this docblock,
 * instead of silently inheriting an answer.
 */
export function countsTowardAttemptsAllowed(status: TestAttemptStatus): boolean {
  switch (status) {
    case 'in_progress':
    case 'submitted':
    case 'passed':
    case 'failed':
    case 'expired':
      return true;
  }
}
